import type { DepositLimitsResponse } from '../../../my-account/SaferGambling/types';
import type { MethodOrInstrument } from '../../../PaymentMethodSelector/types';

type AmountStep = {
  amount: string;
  paymentMethod: string;
  termsAccepted: boolean;
};

type CardDetails = {
  cardNumber: string;
  expiryDate: string;
  cvv: string;
  cardholderName: string;
};

enum CardType {
  VISA = 'visa',
  MASTERCARD = 'mastercard',
  AMEX = 'amex',
}

type MethodStep = CardDetails;

type FormData = {
  amountStep: AmountStep;
  methodStep: MethodStep;
};

type TrackingDetails = {
  provider?: string;
  amount?: string;
  transactionId?: string;
  method?: string;
  offer?: string;
};

type DepositErrorHandler = (errorMessage: string, errorStep: keyof typeof DEPOSIT_ERROR_STEP, transactionStatus?: TransactionStatusResponse, errorKey?: string) => void;

type CardDetailsStepProps = {
  onRegisterBackHandler: (handler: () => boolean) => void;
  onError: DepositErrorHandler;
  onTransactionStart: () => void;
  onRegisterCleanupHandler: (cleanup: () => void) => void;
  namespace: string;
  depositFlowInstance: ReturnType<typeof import('./hooks/useDepositFlow').useDepositFlow>;
  setTrackingDetails: (details?: TrackingDetails) => void;
  bundleCode?: string | null;
  'data-track-container'?: string;
  'data-track-type'?: string;
  'data-track-count'?: string;
};

type DepositAmountStepProps = {
  depositLimits: DepositLimitsResponse | null;
  isDepositLimitsLoading: boolean;
  namespace: string;
  onError: DepositErrorHandler;
  onTransactionStart: () => void;
  depositFlowInstance: ReturnType<typeof import('./hooks/useDepositFlow').useDepositFlow>;
  setTrackingDetails: (details?: TrackingDetails) => void;
  minimumAmountForBonus?: number;
  autoProcessPaymentMethod?: MethodOrInstrument;
  'data-track-container'?: string;
};

type ErrorStepProps = {
  errorMessage: string;
  // TODO: Make this required in scope of adding the progress indicator
  errorStep?: keyof typeof DEPOSIT_ERROR_STEP | null;
  errorKey?: string;
  onRetry: () => void;
  'data-track-status'?: string;
  'data-track-error'?: string;
  'data-track-method'?: string;
  'data-track-provider'?: string;
  'data-track-amount'?: string;
  'data-track-id'?: string;
  'data-track-category'?: string;
  'data-track-type'?: string;
  'data-track-count'?: string;
  'data-track-container'?: string;
};

type SuccessStepProps = {
  onClose: () => void;
  namespace: string;
  'data-track-provider'?: string;
  'data-track-amount'?: string;
  'data-track-id'?: string;
  'data-track-method'?: string;
  'data-track-container'?: string;
  'data-track-type'?: string;
  'data-track-count'?: string;
  'data-track-offer'?: string;
};

type RedirectionStepProps = {
  namespace: string;
  onRegisterCleanupHandler?: (cleanup: () => void) => void;
  depositFlowInstance: ReturnType<typeof import('./hooks/useDepositFlow').useDepositFlow>;
  'data-track-method'?: string;
  'data-track-provider'?: string;
  'data-track-amount'?: string;
  'data-track-id'?: string;
  'data-track-type'?: string;
  'data-track-count'?: string;
  'data-track-offer'?: string;
};

type DepositTransactionPayload = {
  paymentMethod: string;
  idempotencyToken: string;
  amount: number;
  recurringReference?: string;
  promotionId?: string;
  paymentMethodData?: string;
  instrumentData?: string;
  acceptedTermsAndConditions?: boolean;
  seonSessionId?: string;
};

type TransactionRedirectionResponse = {
  payload: {
    redirection: {
      url: string;
      container: 'IFRAME' | 'WINDOW';
      method: 'GET' | 'POST';
      postParams?: object;
    };
  };
};

/** Terminal success states from transaction status long-poll */
const TRANSACTION_STATUS_SUCCESS_STATES = ['SETTLED', 'PERFORMED'] as const;

/** Terminal failure states from transaction status long-poll */
const TRANSACTION_STATUS_FAILURE_STATES = ['FAILED', 'CANCELLED', 'REJECTED', 'EXPIRED', 'REVERSED'] as const;

type TransactionStatusState = 'PENDING' | (typeof TRANSACTION_STATUS_SUCCESS_STATES)[number] | (typeof TRANSACTION_STATUS_FAILURE_STATES)[number];

type TransactionStatusResponse = {
  state: TransactionStatusState;
  errorKey?: string;
  redirectUrl?: string;
  isFirstPayIn: boolean;
  daysSinceFtd: number;
};

type DepositSuccessData = {
  isFirstPayIn: boolean;
  daysSinceFtd: number;
};

type ApplePayMerchantSession = {
  epochTimestamp?: number;
  expiresAt?: number;
  merchantSessionIdentifier?: string;
  nonce?: string;
  merchantIdentifier?: string;
  domainName?: string;
  displayName?: string;
  signature?: string;
  operationalAnalyticsIdentifier?: string;
  retries?: number;
};

type ApplePayValidationRequest = {
  url: string;
  domain: string;
};

type ApplePayErrorResponse = {
  code: number;
  message: string;
  path: string;
};

const DEPOSIT_ERROR_STEP = {
  PROCESSING_DEPOSIT: 'PROCESSING_DEPOSIT',
  PAYMENT_SENT: 'PAYMENT_SENT',
  DEPOSIT_COMPLETE: 'DEPOSIT_COMPLETE',
};

export { CardType, DEPOSIT_ERROR_STEP, TRANSACTION_STATUS_FAILURE_STATES, TRANSACTION_STATUS_SUCCESS_STATES };
export type {
  AmountStep,
  ApplePayErrorResponse,
  ApplePayMerchantSession,
  ApplePayValidationRequest,
  CardDetails,
  CardDetailsStepProps,
  DepositAmountStepProps,
  DepositErrorHandler,
  DepositSuccessData,
  DepositTransactionPayload,
  ErrorStepProps,
  FormData,
  MethodStep,
  RedirectionStepProps,
  SuccessStepProps,
  TrackingDetails,
  TransactionRedirectionResponse,
  TransactionStatusResponse,
  TransactionStatusState,
};
